import { DsrLicenseApi } from "@/api";
import i18n from "@/i18n";
import { showSuccessSnackbar, showErrorSnackbar } from "@/utilities/snackbar";

/** Valore sentinella di crediti illimitati, coerente con MaxEnterprises = -1 lato backend. */
export const UNLIMITED_CREDITS = -1;

export const AccessLevel = {
  Active: "active",
  ReadOnly: "readOnly",
  Expired: "expired",
};

export async function getLicenseState(firmId = null) {
  const { data } = await DsrLicenseApi.getLicenseState(firmId);
  return data;
}

export async function getFirmLicenses(firmId) {
  const { data } = await DsrLicenseApi.getFirmLicenses(firmId);
  return data;
}

export async function registerSubscriptionYear(firmId, payload) {
  try {
    const { data } = await DsrLicenseApi.registerSubscriptionYear(
      firmId,
      payload
    );
    showSuccessSnackbar(200, i18n.t("dsr-license.year-registered"));
    return data;
  } catch (ex) {
    showErrorSnackbar(ex);
    throw ex;
  }
}

export async function removeSubscriptionYear(firmId, yearId) {
  try {
    const { data } = await DsrLicenseApi.removeSubscriptionYear(firmId, yearId);
    showSuccessSnackbar(200, i18n.t("dsr-license.year-removed"));
    return data;
  } catch (ex) {
    showErrorSnackbar(ex);
    throw ex;
  }
}

export async function releaseSlot(firmId, enterpriseId, year) {
  try {
    const { data } = await DsrLicenseApi.releaseSlot(
      firmId,
      enterpriseId,
      year
    );
    showSuccessSnackbar(200, i18n.t("dsr-license.slot-released"));
    return data;
  } catch (ex) {
    showErrorSnackbar(ex);
    throw ex;
  }
}

/** true se lo stato ha crediti illimitati o almeno uno disponibile. */
export function hasCreditsAvailable(state) {
  if (!state) return false;
  return (
    state.availableCredits === UNLIMITED_CREDITS || state.availableCredits > 0
  );
}

/**
 * L'abbinamento (azienda, esercizio) è già acquisito: la creazione non costa crediti.
 * È la stessa regola R1 applicata dal backend, replicata per poter dirlo prima della conferma.
 */
export function isSlotAcquired(state, enterpriseId, year) {
  if (!state?.slots) return false;
  return state.slots.some(
    (s) => s.enterpriseId === enterpriseId && s.year === year
  );
}

/** Costo in crediti della creazione: 0 su abbinamento già acquisito, 1 altrimenti. */
export function creationCost(state, enterpriseId, year) {
  return isSlotAcquired(state, enterpriseId, year) ? 0 : 1;
}

/** Tipi di questionario già attivi sull'abbinamento: R2 ne impedisce la ricreazione. */
export function activeTypesOnSlot(state, enterpriseId, year) {
  const slot = state?.slots?.find(
    (s) => s.enterpriseId === enterpriseId && s.year === year
  );
  return slot?.activeTypes ?? [];
}

/**
 * Anni dichiarabili secondo R3 (anno ≤ annualità massima − 1), dal più recente.
 * Restituisce array vuoto se la firm non ha annualità registrate.
 */
export function reportableYears(state, depth = 5) {
  if (!state?.maxReportableYear) return [];
  return Array.from(
    { length: depth },
    (_, i) => state.maxReportableYear - i
  ).filter((y) => y > 0);
}

export function isReadOnly(state) {
  return state?.accessLevel === AccessLevel.ReadOnly;
}
